<template>
  <div class="avatar-with-frame">
    <div
      class="avatar-container"
      :class="{
        'has-frame': frameConfig,
        'has-image-frame': frameConfig && frameImageUrl
      }"
      :style="containerStyle"
    >
      <!-- 用户头像或渐变头像 -->
      <img
        v-if="!isGradientAvatar"
        :src="displayAvatarUrl"
        class="avatar-image"
        :style="avatarStyle"
      >
      <div
        v-else
        class="avatar-image avatar-gradient"
        :class="gradientAvatarClass"
        :style="avatarStyle"
      >
        <span
          v-if="centerText"
          class="gradient-avatar-text"
          :style="{ fontSize: centerTextFontSize }"
        >{{ centerText }}</span>
      </div>
      
      <!-- 头像框（静态SVG或动态GIF/APNG或CSS渐变） -->
      <div
        v-if="frameConfig"
        class="avatar-frame"
        :class="[frameAnimationClass, frameGradientClass]"
        :style="frameStyle"
      >
        <!-- CSS渐变边框 -->
        <div
          v-if="isCssGradient"
          class="frame-gradient-border"
          :style="gradientBorderStyle"
        />
        <!-- 图片边框 -->
        <img
          v-else-if="frameImageUrl"
          :src="getImageUrl(frameImageUrl)"
          class="frame-image"
          :class="{ 'frame-animated': isAnimatedImage }"
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { User } from '@element-plus/icons-vue'
import { getImageUrl } from '@/utils/imageUrl'

const props = defineProps({
  avatarUrl: {
    type: String,
    default: ''
  },
  frameConfig: {
    type: Object,
    default: null
  },
  size: {
    type: Number,
    default: 64
  },
  /** 渐变头像中心显示文字（由商城商品 centerText 或用户设置带入），如「帅」 */
  centerText: {
    type: String,
    default: ''
  },
  /** 可选：覆盖中心文字字号（例如 '16px'），不传则根据字数自适应 */
  centerTextSize: {
    type: String,
    default: ''
  }
})

// 判断是否为渐变头像
const isGradientAvatar = computed(() => {
  return props.avatarUrl && props.avatarUrl.startsWith('GRADIENT_')
})

// 获取渐变头像的CSS类名
const gradientAvatarClass = computed(() => {
  if (!isGradientAvatar.value) return ''
  const gradientType = props.avatarUrl.replace('GRADIENT_', '').toLowerCase().replace(/_/g, '-')
  return `gradient-avatar-${gradientType}`
})

// 规范：单字 32-48px，双字 20-28px，多字 ≤18px，随头像尺寸等比缩放（基准 64px）
const centerTextFontSize = computed(() => {
  if (props.centerTextSize) return props.centerTextSize
  const text = (props.centerText || '').trim()
  if (!text) return '20px'
  const len = text.length
  const base = 64
  const scale = Math.min(props.size / base, 1.5)
  let px
  if (len === 1) px = Math.round(40 * scale)
  else if (len === 2) px = Math.round(24 * scale)
  else px = Math.round(18 * scale)
  const cap = Math.floor(props.size * 0.5)
  return `${Math.min(px, cap)}px`
})

// 获取显示用的头像URL（如果为空则使用默认头像）
const displayAvatarUrl = computed(() => {
  if (props.avatarUrl && !isGradientAvatar.value) {
    return getImageUrl(props.avatarUrl)
  }
  // 如果是渐变头像，返回空（使用div显示）
  if (isGradientAvatar.value) {
    return ''
  }
  // 使用默认头像
  return '/image/default_avatar.svg'
})

// 解析头像框配置
const frameImageUrl = computed(() => {
  if (!props.frameConfig) return null
  
  // 如果 itemValue 是 JSON 字符串，解析它
  let config = props.frameConfig
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config)
    } catch {
      // 如果不是 JSON，直接作为图片 URL
      return config
    }
  }
  
  // 如果是对象，获取图片 URL
  if (typeof config === 'object' && config !== null) {
    const url = config.imageUrl || config.url || null
    // 如果是CSS渐变标识，返回null（使用CSS生成）
    if (url && url.startsWith('CSS_GRADIENT_')) {
      return null
    }
    return url
  }
  
  return null
})

// 判断是否为CSS渐变边框
const isCssGradient = computed(() => {
  if (!props.frameConfig) return false
  
  let config = props.frameConfig
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config)
    } catch {
      return false
    }
  }
  
  if (typeof config === 'object' && config !== null) {
    const url = config.imageUrl || config.url
    return url && url.startsWith('CSS_GRADIENT_')
  }
  
  return false
})

// 获取渐变边框样式类
const frameGradientClass = computed(() => {
  if (!isCssGradient.value) return ''
  
  let config = props.frameConfig
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config)
    } catch {
      return ''
    }
  }
  
  if (typeof config === 'object' && config !== null) {
    const url = config.imageUrl || config.url
    if (url) {
      const gradientType = url.replace('CSS_GRADIENT_', '').toLowerCase()
      return `frame-gradient-${gradientType}`
    }
  }
  
  return ''
})

// 获取渐变边框样式
const gradientBorderStyle = computed(() => {
  if (!isCssGradient.value) return {}
  
  let config = props.frameConfig
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config)
    } catch {
      return {}
    }
  }
  
  if (typeof config === 'object' && config !== null && config.gradientColors) {
    const colors = config.gradientColors
    if (Array.isArray(colors) && colors.length > 0) {
      const gradient = colors.map((color, index) => {
        const percent = (index / (colors.length - 1)) * 100
        return `${color} ${percent}%`
      }).join(', ')
      return {
        background: `conic-gradient(from 0deg, ${gradient})`
      }
    }
  }
  
  return {}
})

// 判断是否为动画图片（GIF/APNG）
const isAnimatedImage = computed(() => {
  const url = frameImageUrl.value
  if (!url) return false
  return url.toLowerCase().endsWith('.gif') || url.toLowerCase().endsWith('.apng')
})

// 获取动画类型（CSS 动画）
const animationType = computed(() => {
  if (!props.frameConfig) return null
  
  let config = props.frameConfig
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config)
    } catch {
      return null
    }
  }
  
  if (typeof config === 'object' && config !== null) {
    return config.animationType || null
  }
  
  return null
})

// 动画类名
const frameAnimationClass = computed(() => {
  const animType = animationType.value
  if (!animType) return ''
  
  return `frame-animation-${animType}`
})

// 图片框相对头像的放大比例，使框体明显包在头像外侧（与 cosmetics-tokens 中 --cosmetic-frame-scale 一致）
const FRAME_IMAGE_SCALE = 2.0

// 容器样式：有图片框时容器放大以容纳“包在外侧”的框
const containerStyle = computed(() => {
  if (!props.frameConfig || !frameImageUrl.value) {
    return {
      width: `${props.size}px`,
      height: `${props.size}px`
    }
  }
  const outer = Math.round(props.size * FRAME_IMAGE_SCALE)
  return {
    width: `${outer}px`,
    height: `${outer}px`,
    '--avatar-size': `${props.size}px`,
    '--frame-size': `${outer}px`
  }
})

// 头像样式
const avatarStyle = computed(() => {
  return {
    width: `${props.size}px`,
    height: `${props.size}px`
  }
})

// 头像框样式（图片框时使用放大后的尺寸并居中）
const frameStyle = computed(() => {
  const isImageFrame = !!frameImageUrl.value
  const scale = isImageFrame ? FRAME_IMAGE_SCALE : 1
  const frameSize = Math.round(props.size * scale)
  const baseStyle = {
    width: `${frameSize}px`,
    height: `${frameSize}px`
  }
  if (isImageFrame) {
    baseStyle.left = '50%'
    baseStyle.top = '50%'
    baseStyle.transform = 'translate(-50%, -50%)'
  }
  
  // 规范：旋转 ≥4s，呼吸/脉冲 ≥2s；用 token 兜底
  if (props.frameConfig && typeof props.frameConfig === 'object') {
    const config = props.frameConfig
    let duration = config.animationDuration
    if (duration) {
      const minRotate = '4s'
      const minBreathe = '2s'
      const type = (config.animationType || '').toLowerCase()
      if (type === 'rotate' && parseFloat(duration) < 4) duration = minRotate
      if ((type === 'breathe' || type === 'pulse') && parseFloat(duration) < 2) duration = minBreathe
    } else {
      duration = '4s'
    }
    baseStyle['--animation-duration'] = duration
    if (config.animationDelay) baseStyle['--animation-delay'] = config.animationDelay
  }
  baseStyle['--frame-line-width'] = 'var(--cosmetic-frame-line-width, 3px)'
  return baseStyle
})
</script>

<style scoped lang="scss">
.avatar-with-frame {
  display: inline-block;
  
  .avatar-container {
    position: relative;
    display: inline-block;

    /* 有图片框时：头像居中，框体包在头像外侧 */
    &.has-image-frame {
      .avatar-image,
      .avatar-gradient {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: var(--avatar-size) !important;
        height: var(--avatar-size) !important;
      }
    }
    
    .avatar-image {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
      display: block;
    }
    
    // 渐变头像样式
    .avatar-gradient {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background-size: 200% 200%;
      animation: gradient-shift 3s ease infinite;
      color: white;
      font-weight: bold;
      font-size: 20px;
      
      .gradient-avatar-text {
        text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
      }
      
      /* 每个子类都需显式设置 background-size，否则 background 简写会重置父级，导致动画不生效 */
      &.gradient-avatar-rainbow {
        background: linear-gradient(135deg, #FF0000 0%, #FF7F00 14%, #FFFF00 28%, #00FF00 42%, #0000FF 57%, #4B0082 71%, #9400D3 100%);
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }
      
      &.gradient-avatar-neon-pink {
        background: linear-gradient(135deg, #FF1493 0%, #FF69B4 50%, #FF1493 100%);
        background-size: 200% 200%;
        animation: gradient-shift 2s ease infinite;
      }
      
      &.gradient-avatar-gold {
        background: linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%);
        background-size: 200% 200%;
        animation: gradient-shift 2s ease infinite;
      }
      
      &.gradient-avatar-purple {
        background: linear-gradient(135deg, #8B00FF 0%, #4B0082 50%, #8B00FF 100%);
        background-size: 200% 200%;
        animation: gradient-shift 2.5s ease infinite;
      }
      
      &.gradient-avatar-neon-blue {
        background: linear-gradient(135deg, #00CED1 0%, #1E90FF 50%, #00CED1 100%);
        background-size: 200% 200%;
        animation: gradient-shift 2s ease infinite;
      }
      
      &.gradient-avatar-neon {
        background: linear-gradient(135deg, #0066FF 0%, #00FFFF 100%);
        background-size: 200% 200%;
        animation: gradient-shift 2s ease infinite;
      }
      
      &.gradient-avatar-starry {
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }
      
      /* 规范扩展：灰蓝、青蓝、深空、白银·光、黄金·光（设计文档色彩） */
      &.gradient-avatar-grey-blue {
        background: linear-gradient(180deg, #6b7280 0%, #94a3b8 100%);
        background-size: 200% 200%;
        animation: gradient-shift 3.5s ease infinite;
      }
      
      &.gradient-avatar-cyan-blue {
        background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }
      
      &.gradient-avatar-deep-space {
        background: linear-gradient(135deg, #1e293b 0%, #334155 50%, #0f172a 100%);
        background-size: 200% 200%;
        animation: gradient-shift 3.5s ease infinite;
      }
      
      &.gradient-avatar-silver-light {
        background: linear-gradient(135deg, #e5e7eb 0%, #93c5fd 50%, #e5e7eb 100%);
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }
      
      &.gradient-avatar-gold-light {
        background: linear-gradient(135deg, #D4AF37 0%, #1e293b 50%, #FDE68A 100%);
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }
    }
    
    .avatar-frame {
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
      
      .frame-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        
        &.frame-animated {
          // GIF/APNG 动画图片会自动播放
          animation: none; // 图片本身有动画，不需要CSS动画
        }
      }
      
      // CSS渐变边框（规范：线宽 2-4px，用变量控制）
      .frame-gradient-border {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        padding: var(--frame-line-width, 3px);
        background-clip: padding-box;
        position: relative;
        box-shadow: 0 0 var(--cosmetic-frame-glow-blur-max, 10px) rgba(0, 0, 0, 0);
        
        &::before {
          content: '';
          position: absolute;
          --fw: var(--frame-line-width, 3px);
          top: calc(-1 * var(--fw));
          left: calc(-1 * var(--fw));
          right: calc(-1 * var(--fw));
          bottom: calc(-1 * var(--fw));
          border-radius: 50%;
          padding: var(--fw);
          background: inherit;
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          -webkit-mask-composite: xor;
        }
      }
      
      // 不同类型的渐变边框（动效周期：旋转≥4s，呼吸/脉冲≥2s）
      &.frame-gradient-gold {
        .frame-gradient-border {
          background: conic-gradient(from 0deg, #D4AF37 0%, #F59E0B 50%, #D4AF37 100%);
          animation: frameRotate 4s linear infinite;
        }
      }
      
      &.frame-gradient-neon {
        .frame-gradient-border {
          background: conic-gradient(from 0deg, #0066FF 0%, #00FFFF 50%, #0066FF 100%);
          animation: frameBlink 2s ease-in-out infinite;
        }
      }
      
      &.frame-gradient-rainbow {
        .frame-gradient-border {
          background: conic-gradient(from 0deg, #FF0000 0%, #FF7F00 14%, #FFFF00 28%, #00FF00 42%, #0000FF 57%, #4B0082 71%, #9400D3 100%);
          animation: framePulse 2s ease-in-out infinite;
        }
      }
      
      &.frame-gradient-silver {
        .frame-gradient-border {
          background: conic-gradient(from 0deg, #9ca3af 0%, #e5e7eb 50%, #9ca3af 100%);
          animation: frameRotate 5s linear infinite;
        }
      }
      
      &.frame-gradient-data {
        .frame-gradient-border {
          background: conic-gradient(from 0deg, #06b6d4 0%, #0ea5e9 50%, #06b6d4 100%);
          animation: frameRotate 5s linear infinite;
        }
      }
    }
    
    // CSS 动画效果（规范：旋转≥4s，呼吸/脉冲≥2s）
    &.has-frame {
      .avatar-frame {
        &.frame-animation-rotate {
          animation: frameRotate var(--animation-duration, 4s) linear infinite;
        }
        
        &.frame-animation-blink {
          animation: frameBlink var(--animation-duration, 2s) ease-in-out infinite;
        }
        
        &.frame-animation-breathe {
          animation: frameBreathe var(--animation-duration, 2s) ease-in-out infinite;
        }
        
        &.frame-animation-gradient {
          animation: frameGradient var(--animation-duration, 3s) linear infinite;
        }
        
        &.frame-animation-pulse {
          animation: framePulse var(--animation-duration, 2s) ease-in-out infinite;
        }
      }
    }
  }
}

@keyframes gradient-shift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes frameRotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes frameBlink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes frameBreathe {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes frameGradient {
  0% {
    filter: hue-rotate(0deg);
  }
  100% {
    filter: hue-rotate(360deg);
  }
}

@keyframes framePulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}
</style>
